import { LifeBuoy, ShieldCheck, Gauge, Check, ArrowUpRight, TrendingUp, Info } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Parallax, DataCard, FloatPanel } from "./Decor";
import { Tilt } from "@/components/motion/Tilt";
import { BOOKING_PATH } from "@/lib/site";
import { formatMonthlyPrice, MAINTENANCE_KEYS, MAINTENANCE_BUDGET } from "@/lib/pricing";
import { MAINTENANCE_PACKAGE_NAMES } from "@/i18n/translations";
import { useLang } from "@/i18n/LanguageContext";
import { trackClick } from "@/lib/track";

const TIER_ICONS = [LifeBuoy, ShieldCheck, Gauge];

// Pre-fills the contact form package select with this exact (canonical) name.
const selectPackage = (name) => {
  window.dispatchEvent(new CustomEvent("ph:select-package", { detail: name }));
};

export const ServiceMaintenanceSection = () => {
  const { t, lang } = useLang();
  const m = t.maintenance;
  const b = MAINTENANCE_BUDGET;

  const budgetNote = m.budgetNote.replace("{pctMin}", b.pctMin).replace("{pctMax}", b.pctMax);

  return (
    <section id="vedligeholdelse" className="relative scroll-mt-24 py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" />
      {/* Ambient backlight + clean floating decor, parked in the empty band beside
          the left-aligned heading (never over the cards, never clipped). */}
      <div className="glow-orb animate-glow pointer-events-none" style={{ width: 440, height: 440, top: -60, right: "5%", background: "var(--green)", opacity: 0.12 }} />
      <Parallax className="hidden lg:block absolute z-0 right-[4%] top-[70px] pointer-events-none" from={20} to={-16}>
        <FloatPanel title="uptime" className="animate-float-tilt" />
      </Parallax>
      <Parallax className="hidden xl:block absolute z-0 right-[18%] top-[150px] pointer-events-none" from={-16} to={18}>
        <DataCard label="sla" value="99.9% uptime" tone="green" className="animate-float-slow" />
      </Parallax>

      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={m.eyebrow} title={m.title} intro={m.intro} testId="maintenance-heading" />

        <div className="mt-10 flex items-center gap-3">
          <span className="h-px w-8 bg-[var(--green)]" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{m.tiersLabel}</span>
        </div>

        <div className="scene-3d mt-6 grid lg:grid-cols-3 gap-5 items-stretch" data-testid="maintenance-cards">
          {m.tiers.map((tier, i) => {
            const Icon = TIER_ICONS[i];
            const featured = i === 1;
            const canonical = MAINTENANCE_PACKAGE_NAMES[i];
            return (
              <Tilt key={i} className="h-full">
                <article
                  className={`relative rounded-3xl p-7 flex flex-col h-full depth-card ${
                    featured
                      ? "panel border-[var(--line-strong)] shadow-[0_30px_80px_-30px_var(--glow-blue)]"
                      : "glass-card sheen"
                  }`}
                  data-testid={`maintenance-card-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="grid place-items-center h-11 w-11 rounded-2xl border border-[var(--line-strong)] bg-[rgba(61,123,255,0.08)] text-[var(--blue-bright)]">
                      <Icon size={20} />
                    </span>
                    <span
                      className={`font-mono text-[9px] uppercase tracking-widest rounded-full px-2.5 py-1 border ${
                        featured
                          ? "text-[var(--green)] border-[rgba(54,226,160,0.4)]"
                          : "text-[var(--blue-bright)] border-[var(--line-strong)]"
                      }`}
                      data-testid={`maintenance-tag-${i}`}
                    >
                      {tier.tag}
                    </span>
                  </div>

                  <h3 className="mt-5 font-display text-xl font-semibold text-white leading-snug">{tier.name}</h3>
                  <p className="mt-3 text-sm text-white/90 leading-relaxed">{tier.desc}</p>

                  <div className="divider-line my-5" />

                  <ul className="space-y-2.5 flex-1">
                    {tier.includes.map((inc) => (
                      <li key={inc} className="flex items-start gap-3 text-sm text-[var(--white)]">
                        <span className="grid place-items-center h-5 w-5 shrink-0 rounded-md bg-[rgba(54,226,160,0.12)] text-[var(--green)] mt-0.5">
                          <Check size={12} />
                        </span>
                        {inc}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 flex items-center justify-between border-t border-[var(--line)] pt-4">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{m.priceLabel}</span>
                    <span className="font-sans text-base font-bold tracking-normal text-[var(--green)]" data-testid={`maintenance-price-${i}`}>
                      {formatMonthlyPrice(MAINTENANCE_KEYS[i], lang)}
                    </span>
                  </div>

                  <a
                    href="#kontakt"
                    onClick={() => {
                      selectPackage(canonical);
                      trackClick("click_inquire_maintenance");
                    }}
                    className={`mt-5 justify-center ${featured ? "btn-primary" : "btn-ghost"}`}
                    data-testid={`maintenance-card-cta-${i}`}
                  >
                    {m.cardCta} <ArrowUpRight size={16} />
                  </a>
                </article>
              </Tilt>
            );
          })}
        </div>

        {/* Budget context + dual CTA */}
        <div className="scene-3d mt-6">
          <div className="glass-card depth-card sheen rounded-3xl p-7 lg:p-9" data-testid="maintenance-budget">
            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-[rgba(54,226,160,0.1)] text-[var(--green)]">
                    <TrendingUp size={19} />
                  </span>
                  <h3 className="font-display text-xl font-semibold text-white">{m.supportTitle}</h3>
                </div>
                <p className="text-sm md:text-base text-white/90 leading-relaxed">{m.supportBody}</p>
                <div className="flex items-start gap-3">
                  <Info size={16} className="shrink-0 mt-0.5 text-[var(--blue-bright)]" />
                  <p className="text-sm text-muted leading-relaxed" data-testid="maintenance-budget-note">{budgetNote}</p>
                </div>
                <div className="flex items-start gap-3">
                  <Info size={16} className="shrink-0 mt-0.5 text-[var(--green)]" />
                  <p className="text-sm text-muted leading-relaxed">{m.note}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="#kontakt"
                    onClick={() => {
                      selectPackage(MAINTENANCE_PACKAGE_NAMES[1]);
                      trackClick("click_inquire_maintenance");
                    }}
                    className="btn-primary justify-center"
                    data-testid="maintenance-cta-primary"
                  >
                    {m.ctaPrimary} <ArrowUpRight size={18} />
                  </a>
                  <a
                    href={`${BOOKING_PATH}?src=maintenance`}
                    onClick={() => trackClick("book_time")}
                    className="btn-ghost justify-center"
                    data-testid="maintenance-cta-secondary"
                  >
                    {m.ctaSecondary} <ArrowUpRight size={16} />
                  </a>
                </div>
              </div>

              <div className="rounded-2xl border border-[var(--line-strong)] bg-[rgba(8,12,22,0.5)] p-7 text-center" data-testid="maintenance-budget-stat">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{m.pctLabel}</div>
                <div className="mt-3 font-display text-4xl font-bold text-[var(--green)]">
                  {b.pctMin}-{b.pctMax}%
                </div>
                <div className="mt-2 text-xs text-muted leading-relaxed">{m.pctNote}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
